import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SocietePatrimoineHierarchie, SocietePatrimoineHierarchieEntityState } from '@get/api-interfaces';
import { SocieteCaracteristique, SocieteCaracteristiqueEntityState } from '@get/api-interfaces';
import { Patrimoine, PatrimoineEntityState } from '@get/api-interfaces';
import { SocietePatrimoineHierarchieAncetre, SocietePatrimoineHierarchieAncetreEntityState } from '@get/api-interfaces';
import { SocieteComposantRattachement, SocieteComposantRattachementEntityState } from '@get/api-interfaces';
import { SocieteEspaceFamille, SocieteEspaceFamilleEntityState } from '@get/api-interfaces';
import { Societe, SocieteEntityState } from '@get/api-interfaces';
import { PatrimoineHierarchieTemplate, PatrimoineHierarchieTemplateEntityState } from '@get/api-interfaces';
import { findOrCreateSelector } from '@get/services/ngrx-helper';
import { SocietePatrimoineHierarchieState } from '@get/store/states';
import { getRelationSelectors, Selector, SelectSchema } from '@get/utils';

export const societePatrimoineHierarchieRelations: string[] = ['societeCaracteristiques','patrimoines','hierarchieAncetres','hierarchieDescendants','societeComposantRattachements','societeEspaceFamilles','societes','patrimoineHierarchieTemplates',];

export const { selectEntities, selectAll } = SocietePatrimoineHierarchieState.adapter.getSelectors();

export const selectSocietePatrimoineHierarchieState = createFeatureSelector<SocietePatrimoineHierarchieState.IState>(SocietePatrimoineHierarchieState.societePatrimoineHierarchieFeatureKey);

export const selectIsLoadedSocietePatrimoineHierarchie = createSelector(
  selectSocietePatrimoineHierarchieState,
  (state: SocietePatrimoineHierarchieState.IState) => state.isLoaded
);

export const selectIsLoadingSocietePatrimoineHierarchie = createSelector(
  selectSocietePatrimoineHierarchieState,
  (state: SocietePatrimoineHierarchieState.IState) => state.isLoading
);

export const selectIsReadySocietePatrimoineHierarchie = createSelector(
  selectSocietePatrimoineHierarchieState,
  (state: SocietePatrimoineHierarchieState.IState) => !state.isLoading
);

export const selectIsReadyAndLoadedSocietePatrimoineHierarchie = createSelector(
  selectSocietePatrimoineHierarchieState,
  (state: SocietePatrimoineHierarchieState.IState) => state.isLoaded && !state.isLoading
);

export const selectSocietePatrimoineHierarchiesEntities = createSelector(selectSocietePatrimoineHierarchieState, selectEntities);

export const selectSocietePatrimoineHierarchiesArray = createSelector(selectSocietePatrimoineHierarchieState, selectAll);

export const selectIdSocietePatrimoineHierarchiesActive = createSelector(
  selectSocietePatrimoineHierarchieState,
  (state: SocietePatrimoineHierarchieState.IState) => state.actives
);

const societePatrimoineHierarchiesInObject = (societePatrimoineHierarchies: Dictionary<SocietePatrimoineHierarchieEntityState>) => ({ societePatrimoineHierarchies })

const selectSocietePatrimoineHierarchiesEntitiesDictionary = createSelector(selectSocietePatrimoineHierarchiesEntities, societePatrimoineHierarchiesInObject);

const selectAllSocietePatrimoineHierarchiesObject = createSelector(selectSocietePatrimoineHierarchiesEntities, societePatrimoineHierarchies => {
  return hydrateAll({ societePatrimoineHierarchies });
});

const selectOneSocietePatrimoineHierarchieDictionary = (idSocietePatrimoineHierarchie : number) =>
  createSelector(selectSocietePatrimoineHierarchiesEntities, societePatrimoineHierarchies => ({
    societePatrimoineHierarchies: { [idSocietePatrimoineHierarchie]: societePatrimoineHierarchies[idSocietePatrimoineHierarchie] }
  }));

const selectOneSocietePatrimoineHierarchieDictionaryWithoutChild = (idSocietePatrimoineHierarchie : number) =>
  createSelector(selectSocietePatrimoineHierarchiesEntities, societePatrimoineHierarchies => ({
    societePatrimoineHierarchie: societePatrimoineHierarchies[idSocietePatrimoineHierarchie]
  }));

const selectActiveSocietePatrimoineHierarchiesEntities = createSelector(
  selectIdSocietePatrimoineHierarchiesActive,
  selectSocietePatrimoineHierarchiesEntities,
  (actives: number[], societePatrimoineHierarchies: Dictionary<SocietePatrimoineHierarchieEntityState>) => getSocietePatrimoineHierarchiesFromActives(actives, societePatrimoineHierarchies)
);

function getSocietePatrimoineHierarchiesFromActives(
  actives: number[],
  societePatrimoineHierarchies: Dictionary<SocietePatrimoineHierarchieEntityState>
): Dictionary<SocietePatrimoineHierarchieEntityState> {
  return actives.reduce((acc, idActive) => {
    if (societePatrimoineHierarchies[idActive]) {
      acc[idActive] = societePatrimoineHierarchies[idActive];
    }
    return acc;
  }, {} as Dictionary<SocietePatrimoineHierarchieEntityState>);
}

const selectAllSocietePatrimoineHierarchiesSelectors: Dictionary<Selector> = {};
export function selectAllSocietePatrimoineHierarchies(schema: SelectSchema = {}): Selector {
  if (schema.include) {
    return findOrCreateSelector<SocietePatrimoineHierarchie>(
      schema,
      selectAllSocietePatrimoineHierarchiesSelectors,
      selectSocietePatrimoineHierarchiesEntitiesDictionary,
      getRelationSelectors,
      societePatrimoineHierarchieRelations,
      hydrateAll,
      'societePatrimoineHierarchie'
    );
  } else {
    return selectAllSocietePatrimoineHierarchiesObject;
  }
}

export function selectAllSocietePatrimoineHierarchiesDictionary(
  schema: SelectSchema = {},
  customKey: string = 'societePatrimoineHierarchies'
): Selector {
  return createSelector(selectAllSocietePatrimoineHierarchies(schema), result => {
    const res = { [customKey]: {} as Dictionary<SocietePatrimoineHierarchieEntityState> };
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < result.societePatrimoineHierarchies.length; i++) {
      res[customKey][result.societePatrimoineHierarchies[i].idSocietePatrimoineHierarchie] = result.societePatrimoineHierarchies[i];
    }
    return res;
  });
}

export function selectOneSocietePatrimoineHierarchie(
  schema: SelectSchema = {},
  idSocietePatrimoineHierarchie: number
): Selector {
  if (schema.include) {
  const selectors: Selector[] = [selectOneSocietePatrimoineHierarchieDictionary(idSocietePatrimoineHierarchie)];
  selectors.push(...getRelationSelectors(schema, societePatrimoineHierarchieRelations, 'societePatrimoineHierarchie'));
  return (createSelector as any)(...selectors, hydrateOne);
  } else {
    return selectOneSocietePatrimoineHierarchieDictionaryWithoutChild(idSocietePatrimoineHierarchie);
  }
}

export function selectActiveSocietePatrimoineHierarchies(schema: SelectSchema = {}): Selector {
  const selectors: Selector[] = [
    createSelector(selectActiveSocietePatrimoineHierarchiesEntities, societePatrimoineHierarchies => ({
      societePatrimoineHierarchies
    }))
  ];
  selectors.push(...getRelationSelectors(schema, societePatrimoineHierarchieRelations, 'societePatrimoineHierarchie'));
  return (createSelector as any)(...selectors, hydrateAll);
}

interface hydrateArgs {
  societePatrimoineHierarchies: Dictionary<SocietePatrimoineHierarchieEntityState>;
  societes?: Dictionary<SocieteEntityState>;
  patrimoineHierarchieTemplates?: Dictionary<PatrimoineHierarchieTemplateEntityState>;
  societeCaracteristiques?: Dictionary<SocieteCaracteristiqueEntityState>;
  patrimoines?: Dictionary<PatrimoineEntityState>;
  hierarchieAncetres?: Dictionary<SocietePatrimoineHierarchieAncetreEntityState>;
  hierarchieDescendants?: Dictionary<SocietePatrimoineHierarchieAncetreEntityState>;
  societeComposantRattachements?: Dictionary<SocieteComposantRattachementEntityState>;
  societeEspaceFamilles?: Dictionary<SocieteEspaceFamilleEntityState>;
}

export function hydrateAll(...args: hydrateArgs[]): { societePatrimoineHierarchies: (SocietePatrimoineHierarchie | null)[] } {
  const {
    societePatrimoineHierarchies,
    societes,
    patrimoineHierarchieTemplates,
    societeCaracteristiques,
    patrimoines,
    hierarchieAncetres,
    hierarchieDescendants,
    societeComposantRattachements,
    societeEspaceFamilles
  } = args.reduce((acc, value) => ({ ...acc, ...value }), {} as hydrateArgs);

  return {
    societePatrimoineHierarchies: Object.keys(societePatrimoineHierarchies).map(idSocietePatrimoineHierarchie =>
      hydrate(
        societePatrimoineHierarchies[idSocietePatrimoineHierarchie] as SocietePatrimoineHierarchieEntityState,
        societes,
        patrimoineHierarchieTemplates,
        societeCaracteristiques,
        patrimoines,
        hierarchieAncetres,
        hierarchieDescendants,
        societeComposantRattachements,
        societeEspaceFamilles
      )
    )
  };
}

function hydrateOne(...args: hydrateArgs[]): { societePatrimoineHierarchie: SocietePatrimoineHierarchieEntityState | null } {
  const {
    societePatrimoineHierarchies,
    societes,
    patrimoineHierarchieTemplates,
    societeCaracteristiques,
    patrimoines,
    hierarchieAncetres,
    hierarchieDescendants,
    societeComposantRattachements,
    societeEspaceFamilles
  } = args.reduce((acc, value) => ({ ...acc, ...value }), {} as hydrateArgs);

  const societePatrimoineHierarchie = Object.values(societePatrimoineHierarchies)[0];
  return {
    societePatrimoineHierarchie: hydrate(
      societePatrimoineHierarchie as SocietePatrimoineHierarchieEntityState,
      societes,
      patrimoineHierarchieTemplates,
      societeCaracteristiques,
      patrimoines,
      hierarchieAncetres,
      hierarchieDescendants,
      societeComposantRattachements,
      societeEspaceFamilles
    )
  };
}

function hydrate(
  societePatrimoineHierarchie: SocietePatrimoineHierarchieEntityState,
  societeEntities?: Dictionary<SocieteEntityState>,
  patrimoineHierarchieTemplateEntities?: Dictionary<PatrimoineHierarchieTemplateEntityState>,
  societeCaracteristiqueEntities?: Dictionary<SocieteCaracteristiqueEntityState>,
  patrimoineEntities?: Dictionary<PatrimoineEntityState>,
  hierarchieAncetresEntities?: Dictionary<SocietePatrimoineHierarchieAncetreEntityState>,
  hierarchieDescendantsEntities?: Dictionary<SocietePatrimoineHierarchieAncetreEntityState>,
  societeComposantRattachementEntities?: Dictionary<SocieteComposantRattachementEntityState>,
  societeEspaceFamilleEntities?: Dictionary<SocieteEspaceFamilleEntityState>,
): SocietePatrimoineHierarchie | null {
  if (!societePatrimoineHierarchie) {
    return null;
  }

  const societePatrimoineHierarchieHydrated: SocietePatrimoineHierarchieEntityState = { ...societePatrimoineHierarchie };
  if (societeEntities) {
    societePatrimoineHierarchieHydrated.societe = societeEntities[societePatrimoineHierarchie.societe as number] as Societe;
  } else {
    delete societePatrimoineHierarchieHydrated.societe;
  }
  if (patrimoineHierarchieTemplateEntities) {
    societePatrimoineHierarchieHydrated.patrimoineHierarchieTemplate = patrimoineHierarchieTemplateEntities[societePatrimoineHierarchie.patrimoineHierarchieTemplate as number] as PatrimoineHierarchieTemplate;
  } else {
    delete societePatrimoineHierarchieHydrated.patrimoineHierarchieTemplate;
  }

  if (societeCaracteristiqueEntities) {
    societePatrimoineHierarchieHydrated.societeCaracteristiques = ((societePatrimoineHierarchieHydrated.societeCaracteristiques as number[]) || []).map(
      id => societeCaracteristiqueEntities[id]
    ) as SocieteCaracteristique[];
  } else {
    delete societePatrimoineHierarchieHydrated.societeCaracteristiques;
  }

  if (patrimoineEntities) {
    societePatrimoineHierarchieHydrated.patrimoines = ((societePatrimoineHierarchieHydrated.patrimoines as number[]) || []).map(
      id => patrimoineEntities[id]
    ) as Patrimoine[];
  } else {
    delete societePatrimoineHierarchieHydrated.patrimoines;
  }

  if (hierarchieAncetresEntities) {
    societePatrimoineHierarchieHydrated.hierarchieAncetres = ((societePatrimoineHierarchieHydrated.hierarchieAncetres as number[]) || []).map(
      id => hierarchieAncetresEntities[id]
    ) as SocietePatrimoineHierarchieAncetre[];
  } else {
    delete societePatrimoineHierarchieHydrated.hierarchieAncetres;
  }

  if (hierarchieDescendantsEntities) {
    societePatrimoineHierarchieHydrated.hierarchieDescendants = ((societePatrimoineHierarchieHydrated.hierarchieDescendants as number[]) || []).map(
      id => hierarchieDescendantsEntities[id]
    ) as SocietePatrimoineHierarchieAncetre[];
  } else {
    delete societePatrimoineHierarchieHydrated.hierarchieDescendants;
  }

  if (societeComposantRattachementEntities) {
    societePatrimoineHierarchieHydrated.societeComposantRattachements = ((societePatrimoineHierarchieHydrated.societeComposantRattachements as number[]) || []).map(
      id => societeComposantRattachementEntities[id]
    ) as SocieteComposantRattachement[];
  } else {
    delete societePatrimoineHierarchieHydrated.societeComposantRattachements;
  }

  if (societeEspaceFamilleEntities) {
    societePatrimoineHierarchieHydrated.societeEspaceFamilles = ((societePatrimoineHierarchieHydrated.societeEspaceFamilles as number[]) || []).map(
      id => societeEspaceFamilleEntities[id]
    ) as SocieteEspaceFamille[];
  } else {
    delete societePatrimoineHierarchieHydrated.societeEspaceFamilles;
  }

  return societePatrimoineHierarchieHydrated as SocietePatrimoineHierarchie;
}
